import { useState, useEffect, useMemo } from "react";
import { Instant } from "@js-joda/core";
import { ResponsivePie } from "@nivo/pie";
import { AbandonedVehicleRun } from "./api/Application";
import { Api } from "./api/Api";
import { AbandonedVehicleReviewStatus, AbandonedVehicleStatus } from "./api/Network";
import { showInstantLocalDate } from "./api/Date";
import { AbandonedVehicleSmall, expandAbandonedVehicle } from "./loaders/abandonedVehicleCompressed";

// helpers
function fmtDate( inst: Instant ): string {
    return showInstantLocalDate( inst );
}

function isReviewed( s: AbandonedVehicleStatus ): boolean {
    return s === AbandonedVehicleStatus.ReviewedVerified 
        || s === AbandonedVehicleStatus.ReviewedRejected
        || s === AbandonedVehicleStatus.ReviewedUnsure
        || s === AbandonedVehicleStatus.ReviewedRejectedArrived
        || s === AbandonedVehicleStatus.ReviewedRejectedDeparted
        || s === AbandonedVehicleStatus.ReviewedRejectedIdentity
        || s === AbandonedVehicleStatus.ReviewedRejectedNotAVehicle
        || s === AbandonedVehicleStatus.ReviewedRejectedMoved;
}

function isConfirmed( s: AbandonedVehicleStatus ): boolean {
    return s === AbandonedVehicleStatus.ReviewedVerified;
}

function isRejected( s: AbandonedVehicleStatus ): boolean {
    return s === AbandonedVehicleStatus.ReviewedRejected
        || s === AbandonedVehicleStatus.ReviewedUnsure
        || s === AbandonedVehicleStatus.ReviewedRejectedArrived
        || s === AbandonedVehicleStatus.ReviewedRejectedDeparted
        || s === AbandonedVehicleStatus.ReviewedRejectedIdentity
        || s === AbandonedVehicleStatus.ReviewedRejectedNotAVehicle
        || s === AbandonedVehicleStatus.ReviewedRejectedMoved;
}

function isTowed( s: AbandonedVehicleStatus ): boolean {
    return s === AbandonedVehicleStatus.Impounded || s === AbandonedVehicleStatus.Scrapped;
}

// components
const ACCENT = "#0d6efd";

interface StatCardProps {
    label:   string;
    value:   string | number;
    sub?:    string;
    accent?: string;
}

type CustomerReview = "New" | "Allow" | "Enforce";

function customerReviewOf( v: AbandonedVehicleSmall ): CustomerReview {
    switch( v.reviewStatus?.status ) {
        case AbandonedVehicleReviewStatus.NotActionable: return "Allow";
        case AbandonedVehicleReviewStatus.Actionable:    return "Enforce";
        default:                                         return "New";
    }
}

function StatCard( { label, value, sub, accent }: StatCardProps ) {
    return (
        <div style={{
            background:   "#fff",
            borderRadius: 10,
            padding:      "18px 22px",
            flex:          1,
            minWidth:      150,
            borderTop:    `3px solid ${accent ?? ACCENT}`,
            boxShadow:    "0 1px 4px rgba(0,0,0,.07)",
        }}>
            <div style={{ color: "#6c757d", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>{label}</div>
            <div style={{ color: "#212529", fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{value}</div>
            {sub && <div style={{ color: "#adb5bd", fontSize: 11, marginTop: 5 }}>{sub}</div>}
        </div>
    );
}

interface PieSlice {
    id:    string;
    value: number;
    color: string;
}

interface MiniPieProps {
    data:  PieSlice[];
    pct:   number;
    label: string;
}

function MiniPie( { data, pct, label }: MiniPieProps ) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 90, height: 90, flexShrink: 0 }}>
                <ResponsivePie
                    data={data}
                    colors={( d ) => d.data.color}
                    innerRadius={0.68}
                    padAngle={2}
                    cornerRadius={3}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    isInteractive={false}
                    margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                />
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ color: "#212529", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{label}</div>
                {data.map( d => (
                    <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ width: 7, height: 7, borderRadius: "50%", background: d.color }} />
                            <span style={{ color: "#6c757d", fontSize: 12 }}>{d.id}</span>
                        </div>
                        <span style={{ color: "#212529", fontSize: 12, fontWeight: 600 }}>{d.value}</span>
                    </div>
                ) )}
                <div style={{ color: "#adb5bd", fontSize: 11, marginTop: 4 }}>{pct.toFixed( 1 )}%</div>
            </div>
        </div>
    );
}

// dashboard

interface RunStats {
    total:      number;
    verified:   number;
    rejected:   number;
    pending:    number;
    promoted:   number;   // sent to customer review
    crNew:      number;
    crAllowed:  number;
    crEnforce:  number;
}

export function DashboardPage() {
    const [latestRun, setLatestRun] = useState<AbandonedVehicleRun | null>( null );
    const [vehicles,  setVehicles]  = useState<AbandonedVehicleSmall[]>( [] );
    const [loading,   setLoading]   = useState<boolean>( true );
    const [error,     setError]     = useState<string | null>( null );

    useEffect( () => {
        Api.abandonedVehicleRunList().then( result => {
            if( !result.isOk ) {
                setError( result.error || "Failed to load runs" );
                setLoading( false );
                return;
            }

            const latest = [...result.value].sort( ( a, b ) => b.createdWhen.compareTo( a.createdWhen ) )[0];
            if( !latest ) {
                setLoading( false );
                return;
            }
            setLatestRun( latest );

            Api.abandonedVehicleRunVehicleList( latest.abandonedVehicleRunId ).then( vResult => {
                if( vResult.isOk ) {
                    setVehicles( vResult.value.map( expandAbandonedVehicle ) );
                } else {
                    setError( vResult.error || "Failed to load vehicles" );
                }
                setLoading( false );
            } );
        } );
    }, [] );

    const stats = useMemo<RunStats>( () => {
        const total    = vehicles.length;
        const verified = vehicles.filter( v => isConfirmed( v.status.status ) ).length;
        const rejected = vehicles.filter( v => isRejected(  v.status.status ) ).length;
        const pending  = total - verified - rejected;

        let crNew = 0, crAllowed = 0, crEnforce = 0;
        vehicles.forEach( v => {
            if( !v.reviewStatus ) return;   // never promoted to customer review
            const cr = customerReviewOf( v );
            if( cr === "Allow" ) crAllowed++;
            else if( cr === "Enforce" ) crEnforce++;
            else crNew++;
        } );

        return { total, verified, rejected, pending, promoted: crNew + crAllowed + crEnforce, crNew, crAllowed, crEnforce };
    }, [vehicles] );

    if( loading ) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", color: "#6c757d", fontSize: 14 }}>
                Loading dashboard...
            </div>
        );
    }

    if( error ) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", color: "#dc3545", fontSize: 14 }}>
                {error}
            </div>
        );
    }

    if( !latestRun ) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f8f9fa", color: "#6c757d", fontSize: 14 }}>
                No runs yet.
            </div>
        );
    }

    const { total, verified, rejected, pending, promoted, crNew, crAllowed, crEnforce } = stats;
    const reviewed     = verified + rejected;
    const reviewPct    = total    ? ( reviewed / total * 100 )     : 0;
    const crDecided    = crAllowed + crEnforce;
    const crDecidedPct = promoted ? ( crDecided / promoted * 100 ) : 0;

    const reviewPieData: PieSlice[] = [
        { id: "Verified", value: verified, color: "#198754" },
        { id: "Rejected", value: rejected, color: "#dc3545" },
        { id: "Pending",  value: pending,  color: "#e9ecef" },
    ];
    const customerPieData: PieSlice[] = [
        { id: "New",     value: crNew,     color: "#adb5bd" },
        { id: "Allow",   value: crAllowed, color: "#198754" },
        { id: "Enforce", value: crEnforce, color: "#dc3545" },
    ];

    return (
            <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "28px 32px", fontFamily: "system-ui, sans-serif" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <div style={{ color: "#212529", fontSize: 20, fontWeight: 700 }}>Latest Abandoned Vehicles</div>
                        <div style={{ color: "#6c757d", fontSize: 13, marginTop: 4 }}>{fmtDate( latestRun.createdWhen )}</div>
                    </div>
                    <a
                        href={`/detect/${latestRun.abandonedVehicleRunId}`}
                        style={{
                            background:     ACCENT,
                            color:          "#fff",
                            padding:        "10px 18px",
                            borderRadius:   8,
                            fontSize:       13,
                            fontWeight:     600,
                            textDecoration: "none",
                        }}>
                        Review Abandoned Vehicles
                    </a>
                </div>
            <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
                <StatCard label="Detected"         value={total}     sub="In this run"                          accent="#fd7e14" />
                <StatCard label="Pending"          value={pending}   sub="Awaiting annotator review"            accent="#6610f2" />
                <StatCard label="Verified"         value={verified}  sub={`${reviewPct.toFixed( 1 )}% reviewed`} accent="#198754" />
                <StatCard label="Promoted"         value={promoted}  sub="Sent to customer review"              accent="#0d6efd" />
                <StatCard label="Customer Decided" value={crDecided} sub={`${crDecidedPct.toFixed( 1 )}% of promoted`} accent="#198754" />
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <div style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,.07)", flex: 1, minWidth: 280 }}>
                    <MiniPie data={reviewPieData} pct={reviewPct} label="Annotator Review" />
                </div>
                <div style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,.07)", flex: 1, minWidth: 280 }}>
                    <MiniPie data={customerPieData} pct={crDecidedPct} label="Customer Review" />
                </div>
            </div>
            </div>
        );
}